import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useBranding } from '../../contexts/BrandingContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';

export default function Register() {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { branding } = useBranding();

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email.trim(),
        password: formData.password,
        options: {
          data: { 
            full_name: formData.name.trim(),
            phone: formData.phone.trim() || null
          },
        },
      });

      if (signUpError) throw signUpError;
      if (!data?.user) throw new Error('No user returned from sign up.');

      // If session exists, user is auto-logged in (email confirmation disabled)
      if (data.session) {
        const phone = formData.phone.trim() || null;
        
        // Attempt to upsert the profile just in case the DB trigger missed it
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert(
            { id: data.user.id, full_name: formData.name.trim() || 'User', phone: phone, role: 'customer' },
            { onConflict: 'id' }
          );

        // Fallback if phone number is already taken by another account
        if (profileError && profileError.code === '23505' && profileError.message.includes('phone')) {
          await supabase
            .from('profiles')
            .upsert(
              { id: data.user.id, full_name: formData.name.trim() || 'User', phone: null, role: 'customer' },
              { onConflict: 'id' }
            );
        }

        navigate('/app', { replace: true });
      } else {
        // Session is null, meaning email confirmation is required.
        // We cannot insert into 'profiles' here because the user is not authenticated yet (RLS will block it).
        // The secure DB trigger handles it, or the Login page will self-heal it upon first login.
        setSuccess('Account created! Please check your email to confirm before logging in.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      let msg = err.message;
      if (msg.includes('already registered') || msg.includes('already been registered') || msg.includes('User already registered')) {
        msg = 'This email is already registered. Please log in instead.';
      } else if (msg.includes('Password should be at least')) {
        msg = 'Password must be at least 6 characters.';
      } else if (msg.includes('Database error') || msg.includes('unexpected_failure')) {
        msg = 'Account creation failed. Please try again.';
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-brand-light flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md px-4">
        <div className="flex justify-center mb-6">
          <div className="h-16 w-16 bg-brand-navy rounded-2xl flex items-center justify-center shadow-premium transform hover:rotate-12 transition-transform overflow-hidden">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <SafeIcon name={branding.logo_icon || 'Droplet'} className="text-brand-gold text-3xl" />
            )}
          </div>
        </div>
        <h2 className="text-center text-3xl font-extrabold text-brand-navy mb-2">Create Account</h2>
        <p className="text-center text-gray-500 mb-8">Join {branding.app_name}</p>

        <div className="bg-white py-8 px-6 shadow-card rounded-[2.5rem] sm:px-10 border border-gray-100">
          <form className="space-y-5" onSubmit={handleRegister}>
            <Input label="Full Name" name="name" required value={formData.name} onChange={handleChange} placeholder="John Doe" disabled={loading} />
            <Input label="Phone Number" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 98765 43210" disabled={loading} />
            <Input label="Email Address" name="email" type="email" required value={formData.email} onChange={handleChange} placeholder="you@example.com" disabled={loading} />
            <Input label="Password" name="password" type="password" required value={formData.password} onChange={handleChange} placeholder="••••••••" minLength={6} disabled={loading} />
            
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-2xl text-sm border border-red-100 flex items-center gap-3">
                <SafeIcon name="AlertCircle" className="shrink-0 text-lg" />
                <span className="font-medium">{error}</span>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 text-green-700 p-4 rounded-2xl text-sm border border-green-100 flex items-center gap-3">
                <SafeIcon name="CheckCircle" className="shrink-0 text-lg" />
                <span className="font-medium">{success}</span>
              </div>
            )}

            <Button type="submit" size="lg" isLoading={loading} className="w-full h-14 text-lg shadow-premium">
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="font-bold text-brand-navy hover:text-brand-gold transition-colors underline decoration-brand-gold/30 underline-offset-4"
                type="button"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}